import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";
import {
  fetchWalletTransactions,
  selectWalletItems,
  selectWalletStatus,
  selectWalletError,
  selectWalletMeta,
  setWalletPage,
  requestWithdrawal,
} from "../redux/slices/walletSlice";
import { useState } from "react";
import { selectProfile } from "../redux/slices/profileSlice";

function Wallet() {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const userId = profile?.userId;

  const withdrawStatus = useSelector((s) => s.wallet.withdrawStatus);
  const withdrawError = useSelector((s) => s.wallet.withdrawError);
  const withdrawSuccess = useSelector((s) => s.wallet.withdrawSuccess);

  const handleWithdraw = async () => {
    if (!amount || Number(amount) <= 0) return;

    const res = await dispatch(requestWithdrawal({ amount }));

    if (res.meta.requestStatus === "fulfilled") {
      setAmount("");

      setShowModal(false);
      setMessage("");

      dispatch(
        fetchWalletTransactions({
          page: meta.page,
          limit: meta.limit,
        }),
      );
    }
  };

  const items = useSelector(selectWalletItems) || [];
  const status = useSelector(selectWalletStatus);
  const errorMsg = useSelector(selectWalletError);
  const meta = useSelector(selectWalletMeta);

  const loading = status === "loading";

  useEffect(() => {
    dispatch(fetchWalletTransactions({ page: meta.page, limit: meta.limit }));
  }, [dispatch, meta.page, meta.limit]);

  const goPrev = () => dispatch(setWalletPage(Math.max(1, meta.page - 1)));
  const goNext = () =>
    dispatch(setWalletPage(Math.min(meta.totalPages, meta.page + 1)));

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    try {
      return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return iso;
    }
  };

  const formatMoney = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return amount ?? "-";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return String(n);
    }
  };

  // exact API fields
  const getTxnId = (t) =>
    t?.transactionId || t?.txnId || t?.id || t?._id || "-";
  const getUserId = (t) => t?.userId ?? "-";
  const getAmount = (t) => t?.amount ?? 0;
  const getType = (t) => String(t?.type ?? "-").toUpperCase();
  const getSource = (t) => t?.source ?? "-";
  const getLevel = (t) => t?.level ?? "-";
  const getFromUser = (t) => t?.fromUserId ?? "-";
  const getPaymentId = (t) => t?.paymentId ?? "-";
  const getCreatedAt = (t) => t?.createdAt ?? "-";

  return (
    <>
      <Header />

      <main className="pt-3">
        <section className="container">
          <div className="row pt-2">
            <div className="d-flex justify-content-between mb-1">
              <div className="w-100">
                <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                  Wallet Transactions
                </span>
                <h2 className="fw-semibold ls-sm text-success fs-3 lh-1">
                  Total Transactions &mdash; <b>{meta.total}</b> &nbsp;•&nbsp;
                  Page <b>{meta.page}</b> / <b>{meta.totalPages}</b>
                </h2>
                <div className="mt-n3 pt-2">
                  <img src="/assets/images/line-shap.webp" width="150" alt="" />
                </div>
              </div>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-success rounded-pill px-5 border-0 fw-normal py-1"
                  onClick={() => setShowModal(true)}
                >
                  Withdraw
                </button>
                <div
                  className="circle-button bg-success-soft rounded-circle px-2 py-2 ms-2"
                  type="button"
                  onClick={() =>
                    dispatch(
                      fetchWalletTransactions({
                        page: meta.page,
                        limit: meta.limit,
                      }),
                    )
                  }
                  disabled={loading}
                  title="Refresh"
                >
                  <i className="bi bi-arrow-clockwise text-dark"></i>
                </div>
              </div>
            </div>
          </div>

          {errorMsg ? (
            <div className="alert alert-danger py-2">{errorMsg}</div>
          ) : null}

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div
                className="spinner-border"
                role="status"
                aria-hidden="true"
              />
            </div>
          ) : null}

          {!loading && items.length === 0 ? (
            <div className="card rounded-4 border border-light shadow">
              <div className="card-body p-4">
                <h5 className="mb-0 lh-1">No Wallet transactions found</h5>
                <p className="text-muted mb-0">
                  Your wallet transactions will appear here.
                </p>
              </div>
            </div>
          ) : null}

          {/* ✅ Cards grid (same for all screen sizes) */}
          {!loading && items.length > 0 ? (
            <div className="row g-2 py-3">
              {items.map((t, idx) => {
                const isCredit =
                  String(t?.type || "").toLowerCase() === "credit";

                return (
                  <div
                    className="col-lg-3 col-md-6 col-12"
                    key={getTxnId(t) + idx}
                  >
                    <div
                      className="card h-100 shadow-none rounded-4"
                      style={{
                        border: "0.5px solid rgba(0,0,0,0.08)",
                        borderTop: isCredit
                          ? "3px solid #185FA5"
                          : "3px solid #A32D2D",
                        background: isCredit
                          ? "linear-gradient(160deg, #f0f6ff 0%, #ffffff 60%)"
                          : "linear-gradient(160deg, #fff5f5 0%, #ffffff 60%)",
                      }}
                    >
                      <div className="card-body d-flex flex-column gap-3 p-4">
                        {/* Top row: date + id + badge */}
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="d-flex flex-column overflow-hidden">
                            <span
                              className="text-muted text-uppercase fw-medium lh-1"
                              style={{
                                fontSize: "11px",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {formatDateTime(getCreatedAt(t))}
                            </span>
                            <span
                              className="fw-medium text-break font-monospace text-dark"
                              style={{ fontSize: "12px", lineHeight: 1.3 }}
                            >
                              {getTxnId(t)}
                            </span>
                          </div>
                          <span
                            className="badge rounded-pill px-3 py-1 fw-medium flex-shrink-0"
                            style={{
                              fontSize: "11px",
                              background: isCredit ? "#E6F1FB" : "#FCEBEB",
                              color: isCredit ? "#185FA5" : "#A32D2D",
                            }}
                          >
                            {getType(t)}
                          </span>
                        </div>

                        {/* Amount */}
                        <div className="d-flex align-items-baseline gap-2">
                          <span
                            className="text-muted text-uppercase fw-medium"
                            style={{
                              fontSize: "11px",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Amount
                          </span>
                          <span
                            className="fw-medium lh-1"
                            style={{
                              fontSize: "22px",
                              color: isCredit ? "#185FA5" : "#A32D2D",
                            }}
                          >
                            {isCredit ? "+" : "−"}
                            {formatMoney(getAmount(t))}
                          </span>
                        </div>

                        <hr
                          className="my-0"
                          style={{
                            borderColor: isCredit
                              ? "rgba(24,95,165,0.15)"
                              : "rgba(163,45,45,0.15)",
                          }}
                        />

                        {/* Fields */}
                        <div className="d-flex flex-column">
                          {[
                            ["User ID", getUserId(t), true],
                            ["Source", String(getSource(t)), false],
                            ["Level", getLevel(t), false],
                            ["From User", getFromUser(t), true],
                            ["Payment ID", getPaymentId(t), true],
                          ].map(([label, value, mono]) => (
                            <div
                              key={label}
                              className="d-flex justify-content-between align-items-start gap-2"
                              style={{ fontSize: "12px", lineHeight: 1.6 }}
                            >
                              <span className="text-muted flex-shrink-0">
                                {label}
                              </span>
                              <span
                                className={`fw-medium text-end text-break text-capitalize text-dark ${mono ? "font-monospace" : ""}`}
                                style={{ fontSize: "11.5px" }}
                              >
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* Pagination */}
          {!loading && (meta.totalPages > 1 || meta.page > 1) ? (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <button
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={goPrev}
                disabled={meta.page <= 1}
                type="button"
              >
                Prev
              </button>

              <div className="text-secondary">
                Page <b>{meta.page}</b> • Total <b>{meta.total}</b> • Pages{" "}
                <b>{meta.totalPages}</b>
              </div>

              <button
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={goNext}
                disabled={meta.page >= meta.totalPages}
                type="button"
              >
                Next
              </button>
            </div>
          ) : null}
        </section>
      </main>

      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg border-0">
              {/* Header */}
              <div className="modal-header px-5 pb-0 border-0">
                <h4 className="modal-title fw-semibold">
                  💸 Request Withdrawal
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    setMessage("");
                  }}
                />
              </div>

              {/* Body */}
              <div className="modal-body pt-0 px-5">
                <p className="text-muted lh-1 mb-4">
                  Your withdrawal will be credited after admin approval.
                </p>

                <div>
                  {withdrawError && (
                    <div className="alert alert-danger py-2 small mb-2 rounded-3">
                      {withdrawError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="fw-medium mb-1 ms-3">Enter Amount</label>
                  <input
                    type="number"
                    className="form-control rounded-3"
                    placeholder="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>

                {message && <div className="alert py-2 small">{message}</div>}
              </div>

              {/* Footer */}
              <div className="modal-footer border-0 pt-2 d-flex justify-content-center">
                <button
                  className="btn btn-dark rounded-3 px-5 fw-normal"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success rounded-3 px-5 fw-normal"
                  onClick={handleWithdraw}
                  disabled={withdrawStatus === "loading"}
                >
                  {withdrawStatus === "loading"
                    ? "Processing..."
                    : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default Wallet;
